import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import PublicIcon from '@mui/icons-material/Public';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Typography,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import { tokens } from '../../theme';

const QUESTION_TYPE_LABELS = { 1: 'Single Choice', 2: 'Short Answer', 3: 'Rating', 4: 'Multiple Choice' };

const questionIdsKey = (list) => list.map((q) => String(q.id)).join(',');

function QuestionRowContent({ question, index, colors, dragHandleProps, isDragging, isOverlay }) {
  return (
    <Paper
      sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        backgroundColor: colors.primary[400],
        boxShadow: isOverlay ? 6 : isDragging ? 4 : 0,
        border: isOverlay ? `2px solid ${colors.greenAccent[500]}` : `1px solid ${colors.grey[700]}`,
        opacity: isDragging && !isOverlay ? 0.45 : 1,
        borderRadius: 1,
      }}
    >
      <IconButton
        size="small"
        sx={{
          color: colors.grey[300],
          cursor: isOverlay ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
        aria-label="Drag to reorder"
        {...dragHandleProps}
      >
        <DragIndicatorIcon />
      </IconButton>

      <Typography sx={{ minWidth: 28, fontWeight: 'bold', color: colors.greenAccent[400] }}>
        {index + 1}
      </Typography>

      <Box flex={1}>
        <Typography color={colors.grey[100]} fontWeight={600}>
          {question.question}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          {QUESTION_TYPE_LABELS[question.questionType] || 'Unknown'}
        </Typography>
      </Box>

      {!question.country || question.country === '-' ? (
        <Chip
          icon={<PublicIcon style={{ fontSize: 13 }} />}
          label="GLOBAL"
          size="small"
          sx={{ backgroundColor: '#1a6b3c', color: '#fff', fontWeight: 700, fontSize: 11 }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: colors.grey[400] }}>{question.country}</Typography>
      )}
    </Paper>
  );
}

function SortableQuestionRow({ question, index, colors }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: question.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <QuestionRowContent
        question={question}
        index={index}
        colors={colors}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </Box>
  );
}

const FeedbackOrder = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const [allQuestions, setAllQuestions] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [filter, setFilter] = useState('global');
  const [tripFilter, setTripFilter] = useState('all');
  const [savedOrderKey, setSavedOrderKey] = useState('');
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortableIds = useMemo(() => questions.map((q) => q.id), [questions]);
  const isDirty = questions.length > 0 && questionIdsKey(questions) !== savedOrderKey;
  const activeQuestion = activeId != null ? questions.find((q) => q.id === activeId) : null;
  const activeIndex = activeQuestion ? questions.indexOf(activeQuestion) : -1;

  const loadQuestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/feedback`, { headers: { token } });
      const data = await res.json();
      if (data.success && Array.isArray(data.feedbackQuestions)) {
        setAllQuestions(data.feedbackQuestions);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuestions();
  }, []);

  // Filter questions when filter/tripFilter/allQuestions changes
  useEffect(() => {
    let filtered;
    if (filter === 'global') {
      filtered = allQuestions.filter((q) => !q.country || q.country === '-');
    } else if (tripFilter === 'all') {
      filtered = allQuestions.filter((q) => q.country === filter);
    } else if (tripFilter === 'none') {
      filtered = allQuestions.filter((q) => q.country === filter && !q.departureDate);
    } else {
      filtered = allQuestions.filter((q) => q.country === filter && q.departureDate === tripFilter);
    }
    setQuestions(filtered);
    setSavedOrderKey(questionIdsKey(filtered));
  }, [filter, tripFilter, allQuestions]);

  // Unique destination list from all questions
  const destinations = useMemo(() => {
    const seen = new Set();
    return allQuestions
      .filter((q) => q.country && q.country !== '-')
      .filter((q) => { if (seen.has(q.country)) return false; seen.add(q.country); return true; })
      .map((q) => q.country);
  }, [allQuestions]);

  // Unique trips for selected destination
  const tripsForFilter = useMemo(() => {
    if (filter === 'global') return [];
    const seen = new Set();
    return allQuestions
      .filter((q) => q.country === filter && q.departureDate)
      .filter((q) => {
        const d = String(q.departureDate).split('T')[0];
        if (seen.has(d)) return false;
        seen.add(d);
        return true;
      })
      .map((q) => String(q.departureDate).split('T')[0]);
  }, [filter, allQuestions]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    setQuestions((items) => {
      const oldIndex = items.findIndex((q) => q.id === active.id);
      const newIndex = items.findIndex((q) => q.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const saveOrder = async () => {
    if (!questions.length) return;
    setSaving(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_APILINK}/admin/feedback/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', token },
        body: JSON.stringify({ orderedIds: questions.map((q) => q.id) }),
      });
      const data = await res.json();
      setOpenToast(true);
      setToastMessage(data.message || (data.success ? 'Order saved' : 'Failed to save order'));
      if (data.success) {
        setSavedOrderKey(questionIdsKey(questions));
        loadQuestions();
      }
    } catch (e) {
      setOpenToast(true);
      setToastMessage('Error saving question order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header
          title="QUESTION ORDER"
          subtitle="Drag questions to set the order shown in the survey"
        />
        <Link to="/feedback">
          <Button
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Back to questions
          </Button>
        </Link>
      </Box>

      <Box mt={3} maxWidth={720}>

        {/* Destination Filter */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="feedback-order-filter-label" style={{ color: colors.grey[300] }}>
            Show questions for
          </InputLabel>
          <Select
            labelId="feedback-order-filter-label"
            label="Show questions for"
            value={filter}
            onChange={(e) => { setFilter(e.target.value); setTripFilter('all'); }}
          >
            <MenuItem value="global">🌍 Global Questions</MenuItem>
            {destinations.map((dest) => (
              <MenuItem key={dest} value={dest}>📍 {dest}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Trip Filter — show when destination selected and has trip-specific questions */}
        {filter !== 'global' && tripsForFilter.length > 0 && (
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="feedback-trip-filter-label" style={{ color: colors.grey[300] }}>
              Trip
            </InputLabel>
            <Select
              labelId="feedback-trip-filter-label"
              label="Trip"
              value={tripFilter}
              onChange={(e) => setTripFilter(e.target.value)}
            >
              <MenuItem value="all">All trips</MenuItem>
              <MenuItem value="none">No specific trip (destination-level only)</MenuItem>
              {tripsForFilter.map((date) => (
                <MenuItem key={date} value={date}>🗓 {date}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="body2" color={colors.grey[300]}>
            Drag questions by the handle to reorder them.
            {isDirty ? (
              <Typography component="span" color={colors.greenAccent[400]} sx={{ ml: 1 }}>
                Unsaved changes
              </Typography>
            ) : null}
          </Typography>
          <Button
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              '&:hover': { backgroundColor: colors.greenAccent[700] },
              '&.Mui-disabled': { opacity: 0.6 },
            }}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!questions.length || saving || !isDirty}
            onClick={saveOrder}
          >
            {saving ? 'Saving…' : 'Save order'}
          </Button>
        </Box>

        {loading ? (
          <Typography color={colors.grey[300]}>Loading questions…</Typography>
        ) : questions.length === 0 ? (
          <Typography color={colors.grey[300]}>No questions found.</Typography>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveId(active.id)}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <Box display="flex" flexDirection="column" gap={1}>
                {questions.map((question, index) => (
                  <SortableQuestionRow
                    key={question.id}
                    question={question}
                    index={index}
                    colors={colors}
                  />
                ))}
              </Box>
            </SortableContext>
            <DragOverlay>
              {activeQuestion ? (
                <QuestionRowContent
                  question={activeQuestion}
                  index={activeIndex}
                  colors={colors}
                  isOverlay
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        )}
      </Box>

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        action={
          <IconButton size="small" color="inherit" onClick={() => setOpenToast(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default FeedbackOrder;
